import { useCallback, useRef } from 'react';
import {
  ColumnVisibleEvent,
  FilterChangedEvent,
  GridApi,
  GridReadyEvent,
  SelectionChangedEvent,
} from 'ag-grid-community';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  dashboardClientAPIAtom,
  dashboardColumnFilterModelAtom,
  selectedRowsAtom,
} from './atoms';
import { ServerDashboardJob } from './dashboardType';

export const useGridHandlers = () => {
  const gridApiReference = useRef<GridApi | null>(null);
  const setSelectedRows = useSetAtom(selectedRowsAtom);
  const setDashboardClientAPI = useSetAtom(dashboardClientAPIAtom);
  const savedColumnFilterModel = useAtomValue(dashboardColumnFilterModelAtom);
  const setColumnFilterModel = useSetAtom(dashboardColumnFilterModelAtom);

  const onGridReady = useCallback(
    (parameters: GridReadyEvent) => {
      gridApiReference.current = parameters.api;
      setDashboardClientAPI(parameters.api);

      // Restore AG Grid's column filter model so header filter icons and
      // filter panels reflect the persisted filter state.
      if (savedColumnFilterModel) {
        parameters.api.setFilterModel(savedColumnFilterModel);
      }
    },
    [savedColumnFilterModel, setDashboardClientAPI]
  );

  const onColumnVisible = useCallback((event: ColumnVisibleEvent) => {
    const isColumnNowHidden = !event.visible;
    if (!isColumnNowHidden || !event.columns) {
      return;
    }

    for (const column of event.columns) {
      event.api.setColumnFilterModel(column.getColId(), null);
    }
    event.api.onFilterChanged();
  }, []);

  const onSelectionChanged = useCallback(
    (event: SelectionChangedEvent) => {
      const selectedNodes = event.api.getSelectedRows() as ServerDashboardJob[];
      setSelectedRows(selectedNodes);
    },
    [setSelectedRows]
  );

  const onFilterChanged = useCallback(
    (event: FilterChangedEvent) => {
      setColumnFilterModel(event.api.getFilterModel());
    },
    [setColumnFilterModel]
  );

  return {
    gridApiReference,
    onColumnVisible,
    onFilterChanged,
    onGridReady,
    onSelectionChanged,
  };
};
